import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { ConditionalSelect } from '~/eds/src/components/ConditionalSelect/ConditionalSelect';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Section } from '~/eds/src/components/Section/Section';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { ConditionalSelectValue } from '~/eds/src/types/ConditionalSelectValue';
import { ClientContractTypeSelect } from '~/features/client/ClientContractTypeSelect';
import { FieldSectionSelect } from '~/features/fields/FieldSectionSelect/FieldSectionSelect';
import { useErrors } from '~/hooks/useErrors';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import { DataField } from '~/types/Field';
import { PilotId } from '~/types/PilotId';

import { DataFieldType } from '../../../enums/DataFieldType';
import { FieldTypeSelect } from '../FieldTypeSelect/FieldTypeSelect';
import { initialFieldData } from './constants';
import { DefaultFieldValue } from './DefaultFieldValue';
import { FieldConfiguration } from './FieldConfiguration';
import { FieldData } from './types';
import { validateFieldData } from './utils';

interface Props {
  onCreate: (newField: DataField, fieldData: FieldData) => void;
  onHide: () => void;
  acceptFieldTypes?: DataFieldType[];
}

export const CreateFieldPanel = ({
  acceptFieldTypes,
  onCreate,
  onHide,
}: Props) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const { toast } = useToast();
  const { RETRY_ERROR } = useErrors();

  const [createField] = api.endpoints.createField.useMutation();

  const [fieldData, setFieldData] = useState<FieldData>(initialFieldData);

  const {
    defaultValue,
    docRestriction,
    docTypes,
    fieldConfig,
    fieldName,
    fieldSectionId,
    fieldType,
    helpText,
    showOnUploadForm,
  } = fieldData;
  const disableTooltip = validateFieldData(fieldData, intl);

  const handleHide = () => {
    setFieldData(initialFieldData);
    onHide();
  };

  const footer = {
    actions: [
      {
        text: intl.formatMessage({
          id: 'CreateFieldPanel.cancelButtonText',
          defaultMessage: 'Cancel',
          description: 'Text for the cancel button in the Create Field Panel.',
        }),
        onClick: handleHide,
      },
      {
        disabled: Boolean(disableTooltip),
        level: 'primary' as const,
        text: intl.formatMessage({
          id: 'CreateFieldPanel.saveButtonText',
          defaultMessage: 'Save',
          description: 'Text for the save button in the Create Field Panel.',
        }),
        tooltip: disableTooltip,
        onClick: () => {
          createField({ fieldData, formatDate })
            .unwrap()
            .then((newField) => {
              toast({
                message: intl.formatMessage(
                  {
                    id: 'CreateFieldPanel.successMessage',
                    defaultMessage: 'Field "{fieldName}" has been created.',
                    description:
                      'Message when a field is successfully created. Example: fieldName = "Contract Value"',
                  },
                  {
                    fieldName,
                  },
                ),
                status: 'success',
              });
              onCreate(newField!, fieldData);
              handleHide();
            })
            .catch((error) => {
              const message = error?.response?.data.detail ?? RETRY_ERROR;
              toast({
                message: intl.formatMessage(
                  {
                    id: 'CreateFieldPanel.errorMessage',
                    defaultMessage:
                      'Field "{fieldName}" could not be created. {message}',
                    description:
                      'Message when a field creation fails. Example: fieldName = "Contract Value", message = "Invalid field type"',
                  },
                  { fieldName, message },
                ),
                status: 'danger',
              });
            });
        },
      },
    ],
  };

  return (
    <Panel
      animate
      backAction={{
        text: intl.formatMessage({
          id: 'CreateFieldPanel.backActionText',
          defaultMessage: 'Back',
          description: 'Text for the back action in the Create Field Panel.',
        }),
        tooltip: intl.formatMessage({
          id: 'CreateFieldPanel.backActionTooltip',
          defaultMessage: 'Back',
          description: 'Tooltip for the back action in the Create Field Panel.',
        }),
        onClick: handleHide,
      }}
      footer={footer}
      position="right"
      title={intl.formatMessage({
        id: 'CreateFieldPanel.title',
        defaultMessage: 'Create Data Field',
        description: 'Title for the panel to create Data Field.',
      })}
      width="m"
    >
      <Layout preset="sections">
        <Layout preset="form-fields">
          <FormField<string, false>
            label={intl.formatMessage({
              id: 'CreateFieldPanel.fieldNameLabel',
              defaultMessage: 'Field Name',
              description:
                'Label for the field name input in the Create Field Panel.',
            })}
            description={intl.formatMessage({
              id: 'CreateFieldPanel.fieldNameDescription',
              defaultMessage: 'Pick a name to help you identify this field.',
              description:
                'Description for the field name input in the Create Field Panel.',
            })}
            input={TextInput}
            name="field name"
            placeholder={intl.formatMessage({
              id: 'CreateFieldPanel.fieldNamePlaceholder',
              defaultMessage: 'e.g. Indemnification, Warranties',
              description:
                'Placeholder for the field name input in the Create Field Panel.',
            })}
            value={fieldName ?? ''}
            onChange={(updatedFieldName) =>
              setFieldData({
                ...fieldData,
                fieldName: updatedFieldName,
              })
            }
          />
          <FormField<PilotId, false>
            description={intl.formatMessage({
              id: 'CreateFieldPanel.fieldSectionDescription',
              defaultMessage: 'Group new field with other document data.',
              description:
                'Description for the field section input in the Create Field Panel.',
            })}
            input={FieldSectionSelect}
            label={intl.formatMessage({
              id: 'CreateFieldPanel.fieldSectionLabel',
              defaultMessage: 'Field Section',
              description:
                'Label for the field section input in the Create Field Panel.',
            })}
            name="field section"
            value={fieldSectionId}
            onChange={(updatedFieldSectionId) => {
              setFieldData({
                ...fieldData,
                fieldSectionId: updatedFieldSectionId,
              });
            }}
          />
          <FormField<DataFieldType, false>
            label={intl.formatMessage({
              id: 'CreateFieldPanel.fieldTypeLabel',
              defaultMessage: 'Field Type',
              description:
                'Label for the field type input in the Create Field Panel.',
            })}
            name="field type"
            input={FieldTypeSelect}
            inputProps={{
              config: {
                acceptFieldTypes,
                shouldMergeFieldTypes: true,
              },
            }}
            value={fieldType}
            onChange={(updatedFieldType) =>
              setFieldData({
                ...fieldData,
                defaultValue: null,
                fieldConfig: initialFieldData.fieldConfig,
                fieldType: updatedFieldType,
              })
            }
          />
        </Layout>
        {fieldType && (
          <FieldConfiguration
            config={fieldConfig}
            fieldType={fieldType}
            onChange={(updatedFieldConfig) =>
              setFieldData({
                ...fieldData,
                fieldConfig: updatedFieldConfig,
                defaultValue: null, // reset any value affected by the field config
              })
            }
          />
        )}
        <Accordion
          border="none"
          items={[
            {
              content: (
                <Layout preset="form-fields">
                  {fieldType && (
                    <DefaultFieldValue
                      fieldData={fieldData}
                      value={defaultValue}
                      onChange={(updatedDefaultValue) =>
                        setFieldData({
                          ...fieldData,
                          defaultValue: updatedDefaultValue,
                        })
                      }
                    />
                  )}
                  <FormField<string, false>
                    optional
                    description={intl.formatMessage({
                      id: 'CreateFieldPanel.helpTextDescription',
                      defaultMessage:
                        "Describe the field's purpose and offer usage guidance.",
                      description:
                        'Description for the help text input in the Create Field Panel.',
                    })}
                    label={intl.formatMessage({
                      id: 'CreateFieldPanel.helpTextLabel',
                      defaultMessage: 'Help Text',
                      description:
                        'Label for the help text input in the Create Field Panel.',
                    })}
                    name="help text"
                    input={TextInput}
                    value={helpText}
                    onChange={(updatedHelpText) =>
                      setFieldData({
                        ...fieldData,
                        helpText: updatedHelpText ?? '',
                      })
                    }
                  />
                </Layout>
              ),
              title: intl.formatMessage({
                id: 'CreateFieldPanel.optionalConfigurationsTitle',
                defaultMessage: 'Optional Configurations',
                description:
                  'Title for the optional configurations section in the Create Field Panel.',
              }),
            },
          ]}
          padding="none"
        />
        <Section
          description={intl.formatMessage({
            id: 'CreateFieldPanel.displayOptionsDescription',
            defaultMessage:
              'Customize which document types will display this field.',
            description:
              'Description for the display options section in the Create Field Panel.',
          })}
          title={intl.formatMessage({
            id: 'CreateFieldPanel.displayOptionsTitle',
            defaultMessage: 'Display options',
            description:
              'Title for the display options section in the Create Field Panel.',
          })}
        >
          <Layout preset="form-fields">
            <FormField<
              ConditionalSelectValue<
                'NONE' | 'EXCLUDE' | 'INCLUDE',
                string,
                true
              >,
              false
            >
              input={ConditionalSelect}
              inputProps={{
                sourceInput: {
                  name: 'document types',
                  placeholder: intl.formatMessage({
                    id: 'CreateFieldPanel.selectDocumentTypePlaceholder',
                    defaultMessage: 'Specify document type configuration',
                    description:
                      'Placeholder for selecting document types in the Create Field Panel.',
                  }),
                  options: [
                    {
                      label: intl.formatMessage({
                        id: 'CreateFieldPanel.selectDocumentTypeOptionsAll',
                        defaultMessage: 'All document types',
                        description:
                          'Option label for selecting all document types.',
                      }),
                      value: 'NONE',
                    },
                    {
                      label: intl.formatMessage({
                        id: 'CreateFieldPanel.selectDocumentTypeOptionsAllExcept',
                        defaultMessage: 'All document types, except…',
                        description:
                          'Option label for excluding document types.',
                      }),
                      value: 'EXCLUDE',
                    },
                    {
                      label: intl.formatMessage({
                        id: 'CreateFieldPanel.selectDocumentTypeOptionsSpecific',
                        defaultMessage: 'Only specific document types…',
                        description:
                          'Option label for including specific document types.',
                      }),
                      value: 'INCLUDE',
                    },
                  ],
                },
                conditionalInputs: {
                  EXCLUDE: {
                    input: ClientContractTypeSelect,
                    label: 'Exclude',
                  },
                  INCLUDE: {
                    input: ClientContractTypeSelect,
                    label: 'Only',
                  },
                },
              }}
              label={intl.formatMessage({
                id: 'CreateFieldPanel.selectDocumentTypeLabel',
                defaultMessage: 'Document Types',
                description:
                  'Label for selecting document types in the Create Field Panel.',
              })}
              name="document types"
              value={{
                sourceValue: docRestriction,
                conditionalValues: {
                  EXCLUDE: docTypes,
                  INCLUDE: docTypes,
                },
              }}
              onChange={(updatedConditionalValue) => {
                if (updatedConditionalValue?.sourceValue) {
                  const updatedDocRestriction =
                    updatedConditionalValue.sourceValue;
                  const excludeDocTypes =
                    updatedConditionalValue.conditionalValues.EXCLUDE ?? [];
                  const includeDocTypes =
                    updatedConditionalValue.conditionalValues.INCLUDE ?? [];

                  setFieldData({
                    ...fieldData,
                    docRestriction: updatedDocRestriction,
                    docTypes:
                      updatedDocRestriction === 'EXCLUDE'
                        ? excludeDocTypes
                        : includeDocTypes,
                  });
                }
              }}
            />
            <Checkbox
              name="display field on upload"
              option={{
                info: intl.formatMessage({
                  id: 'CreateFieldPanel.displayOnUploadCheckboxDescription',
                  defaultMessage:
                    'Checking this box will cause this field to appear every time a user uploads documents. It’s important to keep this limited to your most important 2-3 fields.',
                  description:
                    'Description for the display on upload checkbox in the Create Field Panel.',
                }),
                label: intl.formatMessage({
                  id: 'CreateFieldPanel.displayOnUploadCheckboxLabel',
                  defaultMessage: 'Display this field on upload',
                  description:
                    'Label for the display on upload checkbox in the Create Field Panel.',
                }),
                value: true,
              }}
              value={showOnUploadForm}
              onChange={(updatedShowOnUploadForm) =>
                setFieldData({
                  ...fieldData,
                  showOnUploadForm: updatedShowOnUploadForm ?? false,
                })
              }
            />
          </Layout>
        </Section>
      </Layout>
    </Panel>
  );
};
